/**
 * Integrytas Group - Licensing (LIC)
 * Page: LIC.STATUS  (read-only)
 *
 * Shows the active tenant's verified module entitlement: which add-on modules are
 * licensed, which are in the catalog but not enabled, and the expiry. The data is
 * the Ed25519-signed entitlement the gateway verified — not a database flag — so
 * it reflects what the central Licence Authority actually granted.
 *
 * Module rows show the full module name (resolved from the prefix registry,
 * INTEGRYTAS.MODULE.PREFIX_REGISTRY) with the short code as a secondary label.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { StatusBadge } from '../../components/common/StatusBadge';
import type { AuthSession } from '../../types/auth';

interface LicStatus {
  licensing_enabled: boolean;
  tenant: { app: string; customer: string } | null;
  available: boolean;
  modules: string[];
  catalog: string[];
  expires_at: string | null;
}

// Last-resort names if the prefix registry is missing an entry.
const FALLBACK_NAMES: Record<string, string> = {
  csm: 'Common Security Module',
  cms: 'Content Management System',
  eds: 'Email Distribution System',
  dms: 'Document Management System',
  gsi: 'Global Search Indexer',
  fdm: 'Framework Dependency Map',
  cbm: 'Circuit Breaker Manager',
  ike: 'Idempotency Key Engine',
  dse: 'Dynamic Shadow-Field Engine',
  udc: 'User-Defined Column Engine',
  phe: 'Pipeline Hook Engine',
  uld: 'UI Layout Delta Engine',
  dad: 'Dynamic API Docs & Sandbox',
  ror: 'Read-Optimization Router',
};

const LIC_GRID_COLS: GridColumnDef[] = [
  { key: 'module', header: 'Module', defaultWidth: 320 },
  { key: 'status', header: 'Status', defaultWidth: 140 },
];

export interface LicStatusPageProps { session: AuthSession }

export function LicStatusPage({ session }: LicStatusPageProps) {
  return (
    <PersonalizationProvider pageCode="LIC.STATUS" session={session}>
      <LicStatusPageInner session={session} />
    </PersonalizationProvider>
  );
}

function LicStatusPageInner({ session }: LicStatusPageProps) {
  const { t } = useTranslation();
  const [s, setS] = useState<LicStatus | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.licStatus<LicStatus>();
      if (e) throw e;
      setS(data ?? null);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };

  // Module display names from the prefix registry (prefix -> full name).
  useEffect(() => {
    void (async () => {
      try {
        const { data } = await apiDb
          .from('csm_system_parameters')
          .select('parameter_value')
          .eq('internal_code', 'INTEGRYTAS.MODULE.PREFIX_REGISTRY');
        const raw = (data as { parameter_value: string }[] | null)?.[0]?.parameter_value;
        if (raw) setNames(JSON.parse(raw) as Record<string, string>);
      } catch { /* fall back to FALLBACK_NAMES / the code */ }
    })();
  }, []);

  useEffect(() => { void reload(); }, []);

  const detail = useExpandedRows<string>();
  const enabled = new Set(s?.modules ?? []);
  const catalog = s?.catalog ?? [];
  const nameOf = (code: string) => names[code] ?? names[code.toLowerCase()] ?? FALLBACK_NAMES[code.toLowerCase()] ?? code;

  // §17.B13 related-children (expand/contract). A catalog module row is a flat
  // read-only entry with no child collection, so the panel is inert here.
  // TODO: wire relations.
  const buildRelatedGroups = (_code: string): RelatedChildGroup[] => [];
  const hasRelated = (code: string) => buildRelatedGroups(code).some(g => g.items.length > 0);

  const filteredCatalog = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter(code => code.toLowerCase().includes(q) || nameOf(code).toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, filter, names]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('lic.title', 'My Licence')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('lic.filter_placeholder', 'Filter modules…')}
          className="h-7 w-60 rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('lic.refresh', 'Refresh')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('lic.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      {error && (
        <ErrorBanner
          message={t('lic.error_banner', "Couldn't load the licence status.")}
          detail={error}
          retry_label={t('common.retry', 'Retry')}
          on_retry={() => void reload()}
          details_label={t('common.details', 'Details')}
        />
      )}

      <div className="flex-1 overflow-auto p-3">
        {!s && !busy && <div className="text-csm-sm text-csm-watermark">{t('lic.no_info', 'No licence information.')}</div>}
        {s && (
          <>
            <div className="mb-3 flex flex-wrap gap-4 text-csm-xs text-csm-ink-soft">
              <span>{t('lic.licensing_label', 'Licensing:')} <b className={s.licensing_enabled ? 'text-green-700' : 'text-csm-watermark'}>{s.licensing_enabled ? t('lic.enforced', 'enforced') : t('lic.not_configured', 'not configured (all on)')}</b></span>
              {s.tenant && <span>{t('lic.tenant_label', 'Tenant:')} <b className="font-mono text-csm-ink">{s.tenant.app}.{s.tenant.customer}</b></span>}
              <span>{t('lic.authority_label', 'Authority:')} <b className={s.available ? 'text-green-700' : 'text-csm-danger'}>{s.available ? t('lic.verified', 'verified') : t('lic.unavailable', 'unavailable')}</b></span>
              <span>{t('lic.expires_label', 'Expires:')} <b className="text-csm-ink">{s.expires_at ? new Date(s.expires_at).toLocaleString() : '—'}</b></span>
            </div>

            <PersonalizedGrid componentKey="grid" columns={LIC_GRID_COLS}>
              {(visibleCols, widths) => (
                <table className="w-max max-w-xl border-collapse text-csm-sm">
                  <thead><tr className="border-b border-csm-border text-left text-csm-2xs uppercase tracking-wide text-csm-watermark">
                    <th className="w-7 px-2 py-1" title={t('lic.col.related', 'Related records')} />
                    {visibleCols.map(col => (
                      <th key={col.key} className="px-2 py-1"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('lic.col.' + col.key, col.header)}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {filteredCatalog.map(code => (
                      <Fragment key={code}>
                      <tr className="border-b border-csm-border-soft">
                        <td className="px-2 py-1 text-center align-middle">
                          <RelatedExpandButton open={detail.isOpen(code)} hasChildren={hasRelated(code)} onToggle={() => detail.toggle(code)} expandLabel={t('lic.related.expand', 'Show related records')} collapseLabel={t('lic.related.collapse', 'Hide related')} tooltip={t('lic.related.tooltip', 'Show related records')} />
                        </td>
                        {visibleCols.map(col => (
                          <td key={col.key} className="px-2 py-1"
                              style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                            {col.key === 'module' && (
                              <>
                                <div className="text-csm-sm text-csm-ink">{nameOf(code)}</div>
                                <div className="font-mono text-csm-2xs text-csm-watermark">{code}{/* fk-display-ok: module's own catalog code, resolved name already shown above */}</div>
                              </>
                            )}
                            {col.key === 'status' && (enabled.has(code)
                              ? <StatusBadge variant="success" label={t('lic.licensed', 'licensed')} />
                              : <StatusBadge variant="neutral" label={t('lic.not_licensed', 'not licensed')} />)}
                          </td>
                        ))}
                      </tr>
                      {detail.isOpen(code) && (
                        <tr>
                          <td colSpan={visibleCols.length + 1} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3">
                            <RelatedChildrenPanel groups={buildRelatedGroups(code)} session={session} />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                    {filteredCatalog.length === 0 && <tr><td colSpan={visibleCols.length + 1} className="px-2 py-6 text-center">
                      <EmptyState variant={filter.trim() ? 'no-match' : 'no-data'} title={t('lic.empty', 'No licensable modules in the catalog.')} />
                    </td></tr>}
                  </tbody>
                </table>
              )}
            </PersonalizedGrid>
            <p className="mt-2 text-csm-2xs text-csm-watermark">{t('lic.note', 'Entitlements are issued by the central Licence Authority and cryptographically verified — they cannot be changed from this database.')}</p>
          </>
        )}
      </div>
    </div>
  );
}
